import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
/**
 * Okazje — strona-hub wyświetlająca wszystkie okazje z mega menu,
 * pogrupowane w trzy sekcje. Brak produktów.
 */
import type { MouseEvent } from 'react';
import { Link } from 'wouter';
import { ChevronRight } from 'lucide-react';
import {
  CakeSlice, PartyPopper, Heart, Flower, Sparkles, HeartHandshake,
  Gift, Cross, UserRound, User, Baby, Star, Flower2, CalendarDays,
} from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { collectionUrl } from '@/lib/collectionUrl';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';

/* ─── Design tokens ────────────────────────────────────────── */
const DARK   = '#2A1F14';
const GOLD   = '#e05590';
const MUTED  = 'rgba(42,31,20,0.50)';
const CREAM  = '#FAF7F2';
const CREAM2 = '#F5EFE6';
const LINE   = 'rgba(210,60,120,0.18)';
const PINK   = '#D75B76';

/* ─── Sekcje okazji ────────────────────────────────────────── */
type OkazjaItem = { label: string; href: string; icon: React.ElementType; desc: string };
type OkazjaSection = { id: string; title: string; icon: React.ElementType; items: OkazjaItem[] };

const SECTIONS: OkazjaSection[] = [
  {
    id: 'okazje',
    title: 'Na jaką okazję',
    icon: Gift,
    items: [
      { label: 'Urodziny',      href: collectionUrl('Urodziny'),      icon: CakeSlice,     desc: 'Świeże bukiety i flower boxy, które sprawią radość solenizantowi. Dostawa tego samego dnia w Lublinie.' },
      { label: 'Imieniny',      href: collectionUrl('Imieniny'),      icon: PartyPopper,   desc: 'Piękne kwiaty z okazji dnia imienin - tradycja i radość w jednym bukiecie. Zamów z dostawą w Lublinie.' },
      { label: 'Rocznica',      href: collectionUrl('Rocznica'),      icon: Heart,         desc: 'Romantyczne kompozycje na wyjątkowy dzień, który warto uczcić. Wyraź uczucia pięknem świeżych kwiatów.' },
      { label: 'Przeprosiny',   href: collectionUrl('Przeprosiny'),   icon: Flower,        desc: 'Kwiaty, które mówią więcej niż słowa - szczery gest pojednania. Dostępne z dostawą w dniu zamówienia.' },
      { label: 'Gratulacje',    href: collectionUrl('Gratulacje'),    icon: Sparkles,      desc: 'Wyjątkowe bukiety na każdy sukces i osiągnięcie - pogratuluj z klasą. Idealny prezent bez okazji i z okazji.' },
      { label: 'Podziękowania', href: collectionUrl('Podziekowania'), icon: HeartHandshake,desc: 'Piękny gest wdzięczności dla bliskich, nauczycieli i współpracowników. Szczere podziękowanie w każdym płatku.' },
      { label: 'Niespodzianka', href: collectionUrl('Niespodzianka'), icon: Gift,          desc: 'Zaskoczyć kogoś bliskiego można zawsze - nie tylko od święta. Dostawa w Lublinie tego samego dnia.' },
      { label: 'Komunia',       href: collectionUrl('Komunia'),       icon: Cross,         desc: 'Delikatne białe i pastelowe kwiaty na Pierwszą Komunię Świętą. Wyjątkowy prezent na ten szczególny dzień.' },
      { label: 'Obrona',        href: collectionUrl('Obrona'),        icon: Sparkles,      desc: 'Wyraź dumę i wsparcie - kwiaty na obronę pracy dyplomowej. Doskonały prezent dla przyszłego magistra lub doktora.' },
      { label: 'Zaręczyny',     href: collectionUrl('Zareczynny'),    icon: HeartHandshake,desc: 'Romantyczne kwiaty towarzyszące propozycji, która zostaje w pamięci na zawsze. Miłość wyrażona każdym płatkiem.' },
    ],
  },
  {
    id: 'dla-kogo',
    title: 'Dla kogo',
    icon: UserRound,
    items: [
      { label: 'Dla Pary Młodej', href: collectionUrl('Dla-pary-mlodej'), icon: Heart,         desc: 'Eleganckie bukiety i kompozycje ślubne na najważniejszy dzień. Piękna oprawa wyjątkowej chwili dla nowożeńców.' },
      { label: 'Dla mamy',        href: collectionUrl('Dla-mamy'),        icon: UserRound,     desc: 'Wyjątkowe kwiaty dla mamy - bo mama zasługuje na to, co najpiękniejsze. Wyraź miłość i wdzięczność.' },
      { label: 'Dla żony',        href: collectionUrl('Dla-zony'),        icon: Heart,         desc: 'Romantyczne bukiety dla żony na co dzień i od święta. Miłość, która rozkwita razem z każdym bukietem.' },
      { label: 'Dla dziewczyny',  href: collectionUrl('Dla-dziewczyny'), icon: HeartHandshake,desc: 'Romantyczne gesty i piękne bukiety, które zachwycają. Dostawa kwiatów w Lublinie tego samego dnia.' },
      { label: 'Dla babci',       href: collectionUrl('Dla-babci'),       icon: UserRound,     desc: 'Ciepłe i piękne kwiaty dla babci - wdzięczność i miłość w każdym płatku. Niespodzianka, która wzruszy.' },
      { label: 'Dla mężczyzny',   href: collectionUrl('Dla-mezczyzny'),  icon: User,          desc: 'Eleganckie i efektowne kompozycje kwiatowe dla mężczyzny - nowoczesne i bez zbędnych ozdób.' },
      { label: 'Dla przyjaciela', href: collectionUrl('Dla-przyjaciela'),icon: UserRound,     desc: 'Gest przyjaźni i życzliwości - kwiaty dla przyjaciela lub przyjaciółki na każdą chwilę.' },
    ],
  },
  {
    id: 'swieta',
    title: 'Nadchodzące święta',
    icon: CalendarDays,
    items: [
      { label: 'Dzień Babci i Dziadka', href: collectionUrl('Dzien-babci-i-dziadka'), icon: UserRound, desc: 'Ciepłe bukiety na 21 i 22 stycznia - dla babci i dziadka z wdzięcznością i miłością.' },
      { label: 'Walentynki',            href: collectionUrl('Walentynki'),             icon: Heart,     desc: 'Czerwone róże i romantyczne kompozycje na Dzień Zakochanych. Wyznaj miłość z pomocą świeżych kwiatów.' },
      { label: 'Dzień Kobiet',          href: collectionUrl('Dzien-kobiet'),           icon: Flower2,   desc: 'Kwiaty na 8 marca - bo każda kobieta zasługuje na wyjątkowy bukiet. Zamów z dostawą w Lublinie.' },
      { label: 'Wielkanoc',             href: collectionUrl('Wielkanoc'),              icon: Flower,    desc: 'Wiosenne tulipany, hiacynty i narcyzy na Wielkanoc. Świeżość i radość odrodzenia w każdym bukiecie.' },
      { label: 'Dzień Matki',           href: collectionUrl('Dzien-matki'),            icon: Heart,     desc: 'Najpiękniejszy prezent dla mamy na jej wyjątkowy dzień. Kwiaty, które wyrażą wszystko, czego słowami wyrazić nie sposób.' },
      { label: 'Dzień Dziecka',         href: collectionUrl('Dzien-dziecka'),          icon: Baby,      desc: 'Kolorowe i radosne kwiaty na Dzień Dziecka. Niespodzianka, która rozświetli buźkę każdego malucha.' },
      { label: 'Dzień Ojca',            href: collectionUrl('Dzien-ojca'),             icon: User,      desc: 'Elegancki gest wdzięczności dla taty. Kompozycje kwiatowe z charakterem - klasyczne i efektowne.' },
      { label: 'Wszystkich Świętych',   href: collectionUrl('Wszystkich-swietych'),    icon: Cross,     desc: 'Spokojne i dostojne kompozycje na Wszystkich Świętych i Dzień Zaduszny. Kwiaty złożone z szacunkiem i pamięcią.' },
      { label: 'Boże Narodzenie',       href: collectionUrl('Boze-narodzenie'),        icon: Star,      desc: 'Świąteczne kompozycje pełne ciepła i zimowego uroku. Kwiaty, które wypełnią dom magią Bożego Narodzenia.' },
    ],
  },
];

/* ─── Karta okazji ─────────────────────────────────────────── */
function OkazjaCard({ item }: { item: OkazjaItem }) {
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '18px 20px',
        background: '#fff',
        border: `1px solid ${LINE}`,
        borderRadius: 12,
        textDecoration: 'none',
        transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s',
        cursor: 'pointer',
      }}
      onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.borderColor = GOLD;
        el.style.boxShadow = `0 4px 18px rgba(210,60,120,0.14)`;
        el.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.borderColor = LINE;
        el.style.boxShadow = 'none';
        el.style.transform = 'translateY(0)';
      }}
    >
      {/* nagłówek karty */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{
          width: 38, height: 38,
          borderRadius: 9,
          background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={17} color={GOLD} strokeWidth={1.6} />
        </span>

        <span style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 15,
          fontWeight: 600,
          color: DARK,
          flexGrow: 1,
          lineHeight: 1.3,
        }}>
          {item.label}
        </span>

        <ChevronRight size={15} color={MUTED} strokeWidth={1.6} style={{ flexShrink: 0 }} />
      </div>

      {/* opis */}
      <p style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 13,
        color: MUTED,
        lineHeight: 1.65,
        margin: 0,
        paddingLeft: 50,
      }}>
        {item.desc}
      </p>
    </Link>
  );
}

/* ─── Sekcja ────────────────────────────────────────────────── */
function Section({ section }: { section: OkazjaSection }) {
  const Icon = section.icon;
  return (
    <section>
      {/* nagłówek sekcji */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 20,
        paddingBottom: 14,
        borderBottom: `1px solid ${LINE}`,
      }}>
        <span style={{
          width: 36, height: 36,
          borderRadius: 9,
          background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={17} color={GOLD} strokeWidth={1.6} />
        </span>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(18px, 2.2vw, 22px)',
          fontWeight: 600,
          color: DARK,
          margin: 0,
          letterSpacing: '0.01em',
        }}>
          {section.title}
        </h2>
      </div>

      {/* siatka kart */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))',
        gap: 12,
      }}>
        {section.items.map(item => (
          <OkazjaCard key={item.href} item={item} />
        ))}
      </div>
    </section>
  );
}

/* ─── Strona ────────────────────────────────────────────────── */
export default function OkazjePage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.okazje);

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>

      {/* Hero — identyczny jak na stronach okazji w collection.tsx ── */}
      <section style={{
        background: '#fff',
        position: 'relative', overflow: 'hidden',
        borderBottom: `1px solid ${LINE}`,
      }}>
        <PageBreadcrumb crumbs={[{ label: 'Okazje' }]} />

        <div style={{
          maxWidth: 1370, margin: '0 auto',
          padding: 'max(clamp(24px,3vw,44px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(20px,2.5vw,32px)',
        }}>
          <h1 className="bk-fade-up bk-d1"
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(42px,6vw,76px)', fontWeight: 600, lineHeight: 1.08, color: DARK, margin: '0 0 20px', letterSpacing: '-0.01em' }}><CmsEditorialText id="editorial.OkazjePage.h1.1" value={"Kwiaty"} section="Wstęp i opisy" label="Nagłówek 1" /><br />
            <span style={{ color: PINK }}>na każdą okazję</span>
          </h1>

          <p className="bk-fade-up bk-d2"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, lineHeight: 1.7, color: MUTED, maxWidth: 440, marginBottom: 32 }}><CmsEditorialText id="editorial.OkazjePage.p.1" value={"Bukiety i kompozycje kwiatowe na każdą wyjątkową chwilę - od urodzin i rocznic po największe święta. Wybierz okazję, a my zadbamy o resztę."} section="Wstęp i opisy" label="Akapit 1" multiline /></p>

        </div>
      </section>

      {/* Treść ────────────────────────────────────────────────── */}
      <div style={{ flexGrow: 1, maxWidth: 1370, margin: '0 auto', width: '100%', padding: 'clamp(32px, 5vw, 64px) var(--page-gutter)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 56 }}>
          {SECTIONS.map(section => (
            <Section key={section.id} section={section} />
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
